import { GetOfficeTree_Out, OfficeTreeNode, OfficesService } from 'src/pehra-ts-client';
import styles from './OfficeTreeView.module.css';
import { useQuery } from '@tanstack/react-query';
import { TreeItem, TreeView } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RemoveIcon from '@mui/icons-material/Remove';
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';
import { useEffect, useState } from 'react';
import { set } from 'react-hook-form';

export interface IOfficeTreeView {
  currentId?: string | null;
  setSelectedOffice?: (office: any) => void;
}

type TOffice = {
  name: string;
  children: Array<string>;
};

interface OfficeNode {
  name: string;
  children: string[];
}

interface FlatNode {
  name: string;
  children?: number[];
  id: number;
  parent: number | null;
}
const OfficeTreeView: React.FC<IOfficeTreeView> = ({ currentId, setSelectedOffice }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');

  const fetchOfficeTreeQuery = useQuery({
    queryKey: ['officeTree'],
    queryFn: () => OfficesService.getOfficeTree()
  });

  const renderTree = (nodeId: string) => {
    const node = fetchOfficeTreeQuery?.data?.offices[nodeId];
    if (!node) return null;

    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={node?.name}
        sx={{ width: '200%' }}
        icon={!node?.children?.length ? <Typography className={styles.removeIcon}>-</Typography> : null}
        onDoubleClick={(event) => {
          event.stopPropagation();
          navigate(`/office/${nodeId}`);
        }}
      >
        {node?.children.map((childId) => renderTree(childId))}
      </TreeItem>
    );
  };

  function findParents(officeId: string, data: GetOfficeTree_Out): string[] {
    const parentIds: string[] = [];
    let currentId: string | null = officeId;

    // Function to find parent by searching through all offices
    function findParentId(childId: string): string | null {
      for (const [id, office] of Object.entries(data.offices)) {
        if (office.children.includes(childId)) {
          return id;
        }
      }
      return null; // Return null if no parent found
    }

    // Loop to collect all parents until no more are found
    while (currentId) {
      const parentId = findParentId(currentId);
      if (parentId) {
        parentIds.push(parentId);
        currentId = parentId;
      } else {
        break;
      }
    }

    return parentIds; // Reverse to show parents from top to bottom
  }

  useEffect(() => {
    if (currentId && fetchOfficeTreeQuery?.data) {
      // get all the parent nodes of the current node
      const parentNodes = [];
      let currentNode = currentId as string;
      const parentIds = findParents(currentNode, fetchOfficeTreeQuery?.data);
      setExpanded(parentIds);
      setSelected(currentId); // Select the current node
      setSelectedOffice?.(fetchOfficeTreeQuery?.data?.offices[currentId]);
    }
  }, [currentId]);

  return (
    <Box className={styles.container}>
      <TreeView
        aria-label="office navigator"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        selected={selected}
        onNodeToggle={(event, nodes) => {
          setExpanded(nodes);
        }}
        onNodeSelect={(event: any, nodeId: any) => {
          setSelected(nodeId);
          setSelectedOffice?.({
            ...fetchOfficeTreeQuery?.data?.offices[nodeId],
            id: nodeId
          });
        }}
      >
        {fetchOfficeTreeQuery?.data?.root_offices.map((rootId) => renderTree(rootId))}
      </TreeView>
    </Box>
  );
};

export default OfficeTreeView;
